import axios from 'axios';
import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import Card from '@mui/material/Card';
import { Stack } from '@mui/material';
import Table from '@mui/material/Table';
import Skeleton from '@mui/material/Skeleton';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';

import { useRouter } from '../../../../routes/hooks';
import { Provider } from '../../../../types/provider';
import { useBoolean } from '../../../../hooks/use-boolean';
import AdminEnrollmentsTableRow from './admin-enrollments-table-row';
import { useSettingsContext } from '../../../../components/settings';
import { LoadingScreen } from '../../../../components/loading-screen';
import { useTable, TableNoData, TableHeadCustom } from '../../../../components/table';
import { ResponseErrorChecker } from '../../../../universal-components/responseErrorChecker';

const TABLE_HEAD = [
  { id:'', label: 'Provider Name' },
  { id:'', label: 'Payer ID' },
  { id:'', label: 'Payer Name' },
  { id:'', label: 'Enrollment Status' },
  { id:'', label: 'Effective Date' }
]


export default function AdminEnrollmentsTable() {
  const settings = useSettingsContext();
  const { logout } = useAuth0();
  const { replace } = useRouter();
  const table = useTable({defaultDense: false, defaultRowsPerPage:25});
  const providersHaveLoaded = useBoolean(false);
  const tableHasLoaded = useBoolean(true);
  const [providers, setProviders] = useState<{label: string, id: string}[]>([]);
  const [supervisedBy, setSupervisedBy] = useState<string>('');
  const [selectedProvidersId, setSelectedProvidersId] = useState<string>('');
  const [enrollments, setEnrollments] = useState<{
      firstName: string;
      lastName: string;
      payerId: string;
      payerName: string;
      enrollmentStatus: string;
      effectiveDate: string;
  }[]>([]);

  useEffect(() => {
    const getTableData = async () => {
      setSupervisedBy('')
      tableHasLoaded.onFalse()
      const response = await ResponseErrorChecker(() => axios.get(`${import.meta.env.VITE_API_URL}/providers/enrollments?providerId=${selectedProvidersId}`), logout, replace);
      if(response !== null) {
        setEnrollments(response.data);

        if(response.data.length > 0) {
          const whosEnrolled = `${response.data[0].firstName} ${response.data[0].lastName}`
          const selectedProvider = providers.find((provider) => provider.id === selectedProvidersId);
          if(whosEnrolled !== selectedProvider?.label)
          {
            setSupervisedBy(`${selectedProvider?.label} is supervised by ${whosEnrolled}`);
          }
        } else {
          setSupervisedBy("No enrollment data is available for this provider");
        }
      }
      tableHasLoaded.onTrue()
    };
    if(selectedProvidersId)
      getTableData();
    else {
      setEnrollments([]);
      setSupervisedBy('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvidersId]);


  useEffect(() => {
    const getTableData = async () => {
      const response = await ResponseErrorChecker(() => axios.get(`${import.meta.env.VITE_API_URL}/providers/admin`), logout, replace);
      if(response !== null) {
        // setProviders(response.data);
        // setTableLength(response.data.length);
        providersHaveLoaded.onTrue();
        setProviders(response.data.map((option: Provider) => ({'label': `${option.firstName} ${option.lastName}`, 'id': option.id})));
      }
    };

    getTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logout, replace]);


  return(
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <Card sx={{p:3}}>
        <Stack>
          <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
            <Typography variant="h4">
              Enrollments
            </Typography>
            {supervisedBy}
            { providersHaveLoaded.value ?
               <Autocomplete
                 options={providers}
                 getOptionLabel={(option) => option.label}
                 onChange={(event, value) => setSelectedProvidersId(value?.id || '')}
                 sx={{ width: '220px' }}
                 renderInput={(params) => <TextField {...params} label="Provider" />}
               />
             :
               <Skeleton sx={{ width:'220px'}}/>
            }
          </Stack>

                { tableHasLoaded.value ?
                  <Table size='small' sx={{ minWidth: 960 }}>
                    <TableHeadCustom
                      headLabel={TABLE_HEAD}
                      rowCount={enrollments.length}
                      order={table.order}
                      orderBy={table.orderBy}
                      onSort={table.onSort}
                    />
                    <TableBody>
                      {enrollments.map((enrollment) => (
                        <AdminEnrollmentsTableRow
                          enrollment={enrollment}
                        />
                      ))}
                    </TableBody>
                    <TableNoData notFound={!tableHasLoaded.value || (tableHasLoaded.value && enrollments.length === 0)} />
                  </Table>
                  :
                  <LoadingScreen sx={{ height:'600px', width:'auto'}}/>
                }

          </Stack>

          </Card>
    </Container>  )


}
